import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button, Input, Tooltip, Typography, Spin, Space, Tag, Card } from 'antd';
import {
  MessageOutlined,
  CloseOutlined,
  SendOutlined,
  RobotOutlined,
  UserOutlined,
  QuestionCircleOutlined,
  ShoppingCartOutlined,
  ThunderboltOutlined,
  StarFilled,
  GlobalOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useLanguage } from '../store/LanguageContext';
import { SUBMIT_PAYMENT_ENABLED } from '../utils/site';

const { Text, Paragraph } = Typography;

// ==================== 类型定义 ====================
interface ToolCard {
  name: string;
  slug: string;
  short_description?: string;
  rating?: number;
  pricing_type?: string;
  availability_cn?: string;
  link: string;
}

interface ActionButton {
  label: string;
  url: string;
  type: 'primary' | 'default' | 'link';
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  toolCards?: ToolCard[];
  actions?: ActionButton[];
  isStreaming?: boolean;
}

// ==================== 主组件 ====================
const AIChatWidget: React.FC = () => {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<any>(null);
  const abortRef = useRef<AbortController | null>(null);

  const isZh = language === 'zh-CN';

  // 滚动到底部
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  // 打开时获取推荐问题
  useEffect(() => {
    if (open && suggestions.length === 0) {
      fetch('/api/chat/suggestions')
        .then(r => r.json())
        .then(data => setSuggestions(data.suggestions || []))
        .catch(() => {});
    }
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open, suggestions.length]);

  // ==================== 发送消息（支持流式 SSE）====================
  const sendMessage = async (text?: string) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;

    const userMessage: ChatMessage = {
      role: 'user',
      content: msg,
      timestamp: Date.now(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    // 构建对话历史
    const historyForApi = messages.slice(-20).map(m => ({
      role: m.role,
      content: m.content,
    }));

    // 使用 SSE 流式接收
    const abortController = new AbortController();
    abortRef.current = abortController;

    // 先添加一个空的 assistant 消息用于流式填充
    const streamMsgId = Date.now();
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: '',
      timestamp: streamMsgId,
      isStreaming: true,
    }]);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: msg,
          history: historyForApi,
          stream: true,
        }),
        signal: abortController.signal,
      });

      // 非 SSE 响应：直接读取 JSON
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        setMessages(prev => prev.map(m =>
          m.timestamp === streamMsgId
            ? {
                ...m,
                content: data.reply || (isZh ? '抱歉，暂时无法回复' : 'Sorry, unable to reply'),
                toolCards: data.toolCards,
                actions: data.actions,
                isStreaming: false,
              }
            : m
        ));
        setLoading(false);
        return;
      }

      // SSE 流式响应
      const reader = response.body?.getReader();
      if (!reader) throw new Error('No body reader');

      const decoder = new TextDecoder();
      let buffer = '';
      let fullContent = '';
      let toolCards: ToolCard[] | undefined;
      let actions: ActionButton[] | undefined;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;
          const payload = trimmed.slice(6);

          if (payload === '[DONE]') continue;

          try {
            const parsed = JSON.parse(payload);
            if (parsed.content) {
              fullContent += parsed.content;
              const snapshot = fullContent;
              setMessages(prev => prev.map(m =>
                m.timestamp === streamMsgId
                  ? { ...m, content: snapshot }
                  : m
              ));
            }
            if (parsed.toolCards) toolCards = parsed.toolCards;
            if (parsed.actions) actions = parsed.actions;
            if (parsed.error) {
              fullContent += `\n\n⚠️ ${parsed.error}`;
            }
          } catch {
            // 非 JSON，忽略
          }
        }
      }

      // 流结束，最终更新
      setMessages(prev => prev.map(m =>
        m.timestamp === streamMsgId
          ? {
              ...m,
              content: fullContent || (isZh ? '抱歉，暂时无法回复' : 'Sorry, unable to reply'),
              toolCards,
              actions,
              isStreaming: false,
            }
          : m
      ));
    } catch (err: any) {
      if (err.name === 'AbortError') {
        setMessages(prev => prev.map(m =>
          m.timestamp === streamMsgId
            ? { ...m, content: isZh ? '已取消' : 'Cancelled', isStreaming: false }
            : m
        ));
      } else {
        setMessages(prev => prev.map(m =>
          m.timestamp === streamMsgId
            ? {
                ...m,
                content: isZh ? '网络连接出现问题，请稍后重试 😅' : 'Network error, please try again 😅',
                isStreaming: false,
              }
            : m
        ));
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
    }
  };

  // 取消正在进行的流
  const cancelStream = () => {
    abortRef.current?.abort();
  };

  // ==================== 渲染 Markdown（链接、加粗、列表）====================
  const renderContent = (content: string) => {
    const parts = content.split(/(\[.*?\]\(.*?\)|\*\*.*?\*\*)/g);
    return parts.map((part, i) => {
      const linkMatch = part.match(/\[(.*?)\]\((.*?)\)/);
      if (linkMatch) {
        return (
          <a key={i} href={linkMatch[2]}
            style={{ color: '#1677ff', textDecoration: 'underline' }}
            onClick={(e) => {
              // 站内链接直接跳转
              if (linkMatch[2].startsWith('/')) {
                e.preventDefault();
                window.location.href = linkMatch[2];
              }
            }}>
            {linkMatch[1]}
          </a>
        );
      }
      const boldMatch = part.match(/\*\*(.*?)\*\*/);
      if (boldMatch) {
        return <strong key={i}>{boldMatch[1]}</strong>;
      }
      return <span key={i}>{part}</span>;
    });
  };

  // ==================== 渲染工具卡片 ====================
  const renderToolCards = (cards: ToolCard[]) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
      {cards.map((tool, i) => (
        <Card
          key={i}
          size="small"
          hoverable
          style={{
            borderRadius: 10,
            border: '1px solid #e8e8e8',
            cursor: 'pointer',
          }}
          bodyStyle={{ padding: '10px 12px' }}
          onClick={() => { window.location.href = tool.link; }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2 }}>
                {tool.name}
                {tool.pricing_type === 'free' && (
                  <Tag color="green" style={{ marginLeft: 6, fontSize: 10, lineHeight: '16px' }}>Free</Tag>
                )}
              </div>
              <Text type="secondary" style={{ fontSize: 11, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {tool.short_description}
              </Text>
            </div>
            <div style={{ textAlign: 'right', marginLeft: 8, flexShrink: 0 }}>
              {tool.rating && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <StarFilled style={{ color: '#faad14', fontSize: 11 }} />
                  <span style={{ fontSize: 11, fontWeight: 600 }}>{tool.rating}</span>
                </div>
              )}
              {tool.availability_cn && (
                <Tag
                  color={tool.availability_cn === 'direct' || tool.availability_cn === 'cn_native' ? 'green' : 'orange'}
                  style={{ fontSize: 9, marginTop: 2, borderRadius: 6 }}
                >
                  {tool.availability_cn === 'cn_native' ? '🇨🇳 原生' : tool.availability_cn === 'direct' ? '✅ 可访问' : tool.availability_cn}
                </Tag>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );

  // ==================== 渲染行动按钮 ====================
  const renderActions = (btns: ActionButton[]) => (
    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
      {btns.map((btn, i) => (
        <Button
          key={i}
          type={btn.type === 'primary' ? 'primary' : btn.type === 'link' ? 'link' : 'default'}
          size="small"
          style={{ borderRadius: 16, fontSize: 12 }}
          onClick={() => { window.location.href = btn.url; }}
        >
          {btn.label}
        </Button>
      ))}
    </div>
  );

  // ==================== UI ====================
  return (
    <>
      {/* 悬浮按钮 */}
      {!open && (
        <Tooltip title={isZh ? '💬 AI 智能客服' : '💬 AI Assistant'} placement="left">
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={<MessageOutlined />}
            onClick={() => setOpen(true)}
            className="ai-chat-fab"
            style={{
              position: 'fixed',
              right: 24,
              bottom: 24,
              width: 56,
              height: 56,
              fontSize: 24,
              zIndex: 1000,
              boxShadow: '0 4px 20px rgba(22, 119, 255, 0.4)',
              background: 'linear-gradient(135deg, #1677ff, #4096ff)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        </Tooltip>
      )}

      {/* 聊天窗口 */}
      {open && (
        <div className="ai-chat-window" style={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          width: 400,
          height: 600,
          borderRadius: 16,
          overflow: 'hidden',
          boxShadow: '0 8px 40px rgba(0,0,0,0.15)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          background: '#fff',
          border: '1px solid #e8e8e8',
        }}>
          {/* Header */}
          <div style={{
            padding: '14px 16px',
            background: 'linear-gradient(135deg, #1677ff, #4096ff)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Space size={8}>
              <RobotOutlined style={{ fontSize: 20 }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>
                  {isZh ? '小G · AI 智能客服' : 'AI Assistant'}
                </div>
                <div style={{ fontSize: 11, opacity: 0.85 }}>
                  <ThunderboltOutlined /> DeepSeek {isZh ? '驱动 · 智能问答' : 'Powered · Smart Q&A'}
                </div>
              </div>
            </Space>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setOpen(false)}
              style={{ color: '#fff' }}
              size="small"
            />
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '12px 14px',
            background: '#f5f7fa',
          }}>
            {/* 欢迎消息 */}
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <RobotOutlined style={{ fontSize: 40, color: '#1677ff', marginBottom: 12 }} />
                <Paragraph style={{ color: '#666', marginBottom: 16 }}>
                  {isZh
                    ? '您好！我是 GlobalAI Tools 智能助手小G 🤖\n有什么可以帮您的？'
                    : 'Hi! I\'m the GlobalAI Tools AI assistant 🤖\nHow can I help you?'}
                </Paragraph>

                {/* 推荐问题标签 */}
                {suggestions.length > 0 && (
                  <div style={{ textAlign: 'left' }}>
                    <Text type="secondary" style={{ fontSize: 12, marginBottom: 8, display: 'block' }}>
                      <QuestionCircleOutlined /> {isZh ? ' 您可以试试这些问题：' : ' Try these questions:'}
                    </Text>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {suggestions.slice(0, 6).map((s, i) => (
                        <Tag
                          key={i}
                          color="blue"
                          style={{ cursor: 'pointer', borderRadius: 12, marginBottom: 4, fontSize: 12 }}
                          onClick={() => sendMessage(s)}
                        >
                          {s}
                        </Tag>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 对话消息 */}
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: 12,
                }}
              >
                <div style={{
                  maxWidth: '88%',
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user'
                    ? '16px 16px 4px 16px'
                    : '16px 16px 16px 4px',
                  background: msg.role === 'user'
                    ? 'linear-gradient(135deg, #1677ff, #4096ff)'
                    : '#fff',
                  color: msg.role === 'user' ? '#fff' : '#333',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                  fontSize: 13,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  <div style={{
                    fontSize: 10,
                    opacity: 0.6,
                    marginBottom: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}>
                    {msg.role === 'user'
                      ? <><UserOutlined /> {isZh ? '您' : 'You'}</>
                      : <><RobotOutlined /> {isZh ? '小G' : 'AI'}</>}
                    {msg.isStreaming && (
                      <span style={{ marginLeft: 4, color: '#1677ff' }}>
                        <ThunderboltOutlined /> {isZh ? '生成中...' : 'Generating...'}
                      </span>
                    )}
                  </div>
                  {renderContent(msg.content)}
                  {msg.isStreaming && !msg.content && <Spin size="small" />}

                  {/* 工具卡片 */}
                  {msg.toolCards && msg.toolCards.length > 0 && renderToolCards(msg.toolCards)}

                  {/* 行动按钮 */}
                  {msg.actions && msg.actions.length > 0 && renderActions(msg.actions)}
                </div>
              </div>
            ))}

            {/* Loading 打字动画 */}
            {loading && messages.length > 0 && !messages[messages.length - 1]?.isStreaming && (
              <div style={{ display: 'flex', marginBottom: 12 }}>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '16px 16px 16px 4px',
                  background: '#fff',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                }}>
                  <Spin size="small" />
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                    {isZh ? '正在检索知识库...' : 'Searching knowledge base...'}
                  </Text>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* 快捷操作栏 */}
          <div style={{
            padding: '6px 14px',
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            background: '#fff',
          }}>
            <Tag
              color="orange"
              style={{ cursor: 'pointer', borderRadius: 10, fontSize: 11, margin: 0 }}
              onClick={() => sendMessage(isZh ? '如何提交工具？' : 'How to submit a tool?')}
            >
              <ShoppingCartOutlined /> {isZh ? '提交工具' : 'Submit'}
            </Tag>
            <Tag
              color="green"
              style={{ cursor: 'pointer', borderRadius: 10, fontSize: 11, margin: 0 }}
              onClick={() => sendMessage(
                SUBMIT_PAYMENT_ENABLED
                  ? (isZh ? '收费方案有哪些？' : 'What are the pricing plans?')
                  : (isZh ? '现在可以免费提交工具吗？' : 'Can I submit a tool for free?')
              )}
            >
              💰 {SUBMIT_PAYMENT_ENABLED
                ? (isZh ? '收费方案' : 'Pricing')
                : (isZh ? '免费提交' : 'Free submit')}
            </Tag>
            <Tag
              color="blue"
              style={{ cursor: 'pointer', borderRadius: 10, fontSize: 11, margin: 0 }}
              onClick={() => sendMessage(isZh ? '推荐几个国内可用的 AI 工具' : 'Recommend AI tools available in China')}
            >
              <GlobalOutlined /> {isZh ? '国内可用' : 'CN Available'}
            </Tag>
            <Tag
              color="purple"
              style={{ cursor: 'pointer', borderRadius: 10, fontSize: 11, margin: 0 }}
              onClick={() => sendMessage(isZh ? '合规评估怎么看？' : 'How does compliance work?')}
            >
              <CheckCircleOutlined /> {isZh ? '合规' : 'Compliance'}
            </Tag>
          </div>

          {/* 输入框 */}
          <div style={{
            padding: '10px 14px',
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            gap: 8,
            background: '#fff',
          }}>
            <Input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onPressEnter={() => sendMessage()}
              placeholder={isZh ? '输入您的问题...' : 'Type your question...'}
              disabled={loading}
              style={{ borderRadius: 20 }}
              maxLength={2000}
            />
            {loading ? (
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={cancelStream}
                style={{ borderRadius: 20, minWidth: 40 }}
                title={isZh ? '取消' : 'Cancel'}
              />
            ) : (
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => sendMessage()}
                disabled={!input.trim()}
                style={{ borderRadius: 20, minWidth: 40 }}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatWidget;
